
import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
    Breadcrumbs,
    Grid,
    Typography,
    Table,
    TableContainer,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    Chip,
    Stack,
    Tooltip,
    IconButton,
    Switch,
    Button,
} from '@mui/material';
import { Home as HomeIcon, Visibility,Add,} from '@mui/icons-material';
import PageTitle from '../../../common/PageTitle';
import LinkNavegacao from '../../../common/Link';
import {WritingDownIcon , EditIcon} from '../../../common/Icons';
import { FiltersButton } from '../../../common/FiltersButton';
import { LoadingTableRow } from '../../../common/LoadingTableRow';
import { EmptyTableRow } from '../../../common/EmptyTableRow';
import { CustomTablePagination } from '../../../common/CustomTablePagination';
import { ContractFilter } from './components/ContractFilter';
import { useCommonItems } from '../../../../contexts/CommonItensProvider';
import { getContrato, updateContrato } from '../../management/contract/functions';
import {formatarMoeda, formatarData} from './configs/functions';
import { hasPermission, checkPermissionsAndRedirect } from '../../../../configs/functions';
import { ContractForm } from './components/ContractForm';
import { useSearchParams } from 'react-router-dom';
import { TooltipSelectDisabled } from '../../../common/TooltipSelectDisabled';
import { SettingChipContract } from './components/SettingChipContract';

const isAtivo = (status) => status === 1 || status === true;

const Row = ({ contrato, actionsAllowed, handleOpenEditForm, onSuccess }) => {
    const { exibirAlerta } = useCommonItems();

    const [active, setActive] = useState(isAtivo(contrato.status));
    const [isUpdating, setIsUpdating] = useState(false);

    useEffect(() => {
        setActive(isAtivo(contrato.status));
    }, [contrato.status]);

    const canUpdate = hasPermission(['admin', 'admin_empresas', 'update_empresas']);

    const handleToggleContractStatus = async (event) => {
        if (!canUpdate) {
            exibirAlerta('Erro', 'Você não tem permissão para executar esta ação.', 'error');
            return;
        }

        const novoAtivo = event.target.checked;

        const novoStatus =
            typeof contrato.status === 'number'
                ? novoAtivo ? 1 : 0
                : novoAtivo ? 'ativa' : 'inativa';

        setActive(novoAtivo); 
        setIsUpdating(true);

        try {
            const response = await updateContrato({ id: contrato.id, status: novoStatus });

            exibirAlerta(
                'Sucesso',
                novoAtivo ? 'Contrato ativado com sucesso!' : 'Contrato desativado com sucesso!',
                'success'
            );

            onSuccess?.();
        } catch (error) {
            setActive(!novoAtivo); 
            exibirAlerta(
                'Erro',
                error?.response?.data?.error || 'Erro ao atualizar contrato.',
                'error'
            );
        } finally {
            setIsUpdating(false);
        }
    };
    const chipConfig = SettingChipContract(contrato);

    return (
        <TableRow>
            <TableCell align="left" sx={{ fontSize: '0.9rem' }}>
                {contrato?.nome}
            </TableCell>

            <TableCell align="left">
                {contrato
                    ? `${formatarData(contrato.inicio_contrato)} - ${formatarData(contrato.fim_contrato)}`
                    : '-'}
            </TableCell>

            <TableCell align="left">
                <Chip size="small" variant="outlined" label={chipConfig.label} color={chipConfig.color} />
            </TableCell>

            <TableCell sx={{ fontWeight: 600 }} align="left">
                {formatarMoeda(contrato?.mensalidade)}
            </TableCell>

            <TableCell align="left">{contrato?.qtd_colaboradores ?? 0}</TableCell>

            <TableCell align="left">{contrato?.qtd_colaboradores_admin ?? 0}</TableCell>

            <TableCell>
                <Switch
                    color="primary"
                    checked={active}
                    onChange={handleToggleContractStatus}
                    disabled={!canUpdate || isUpdating}
                />
            </TableCell>

            <TableCell align="center">
                {hasPermission(['admin', 'admin_empresas', 'delete_empresas']) && (
                    <Tooltip title={actionsAllowed ? 'Editar Contrato' : 'Visualizar Contrato'}>
                        <IconButton aria-label="Editar" onClick={handleOpenEditForm} size="small">
                            {actionsAllowed ? <EditIcon /> : <Visibility color="primary" />}
                        </IconButton>
                    </Tooltip>
                )}
            </TableCell>
        </TableRow>
    );
};

export function ListOfContracts() {
    const { exibirAlerta } = useCommonItems();

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [numTotalItems, setNumTotalItems] = useState(0);
    const [contratos, setContratos] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isOpenFilters, setIsOpenFilters] = useState(false);
    const [activeFilters, setActiveFilters] = useState(0);

    const [isOpenEditForm, setIsOpenEditForm] = useState(false);
    const [selectedContrato, setSelectedContrato] = useState(null);
    const [actionsAllowed, setActionsAllowed] = useState(true);
    const [searchParams] = useSearchParams();
    const nome = searchParams.get('nome');

    const handleOpenEditForm = (contrato) => {
        setSelectedContrato(contrato);
        setIsOpenEditForm(true);
    };

    const handleOpenCreateForm = () => {
        setSelectedContrato(null);
        setIsOpenEditForm(true);
    };

    async function carregaContratos() {
        const timer = setTimeout(() => setIsLoading(true), 500);

        try {
            const res = await getContrato(
                { page, perPage: rowsPerPage,
                    isFilter: true,
                    ...(nome ? { nome } : {})});

            setContratos(res?.data.data ?? []);
            setNumTotalItems( res?.data.total ?? 0);
        } catch (error) {
            exibirAlerta('Erro', 'Erro ao carregar contratos', 'error');
            setContratos([]);
            setNumTotalItems(0);
        } finally {
            clearTimeout(timer);
            setIsLoading(false);
        }
    }

    useEffect(() => {
        carregaContratos();
    }, [page, rowsPerPage, nome]);

    useEffect(() => {
        setPage(0);
    }, [nome]);

    useEffect(() => {
        const requiredPermissionsView = [
            'admin',
            'admin_contratos',
            'list_contratos',
        ];

        checkPermissionsAndRedirect(requiredPermissionsView);
    }, []);

    return (
        <>
            <Helmet title="Contratos" defer={false} />

            <Grid
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="end"
                spacing={3}
            >
                <Grid item xs={12} md={9}>
                    <Breadcrumbs>
                        <LinkNavegacao to="/">
                            <HomeIcon fontSize="small" />
                        </LinkNavegacao>

                        <Typography variant="span">
                            Gerenciamento
                        </Typography>

                        <Typography variant="span">
                            Contratos
                        </Typography>
                    </Breadcrumbs>

                    <PageTitle
                        icon={<WritingDownIcon  fontSize="large" />}
                        title="Contratos"
                        description="Administre Contratos e seus detalhes"
                    />
                </Grid>

                <Grid item xs={12}>
                    <Stack
                        direction={{
                            xs: 'column-reverse',
                            md: 'row',
                        }}
                        spacing={2}
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <FiltersButton
                            isOpenFilters={isOpenFilters}
                            setIsOpenFilters={setIsOpenFilters}
                            activeFilters={activeFilters}
                        />

                        {hasPermission(["admin", "admin_empresas", "create_empresas"]) &&
                            <Grid item xs={12} md={3}>
                                <Typography
                                    sx={{
                                        textAlign: {
                                            xs: 'center',
                                            md: 'right'
                                        }
                                    }}
                                >
                                    <TooltipSelectDisabled>
                                        <Button
                                            variant="contained"
                                            startIcon={<Add />}
                                            onClick={handleOpenCreateForm}
                                            disabled={!hasPermission(["admin", "admin_empresas", "create_empresas"])}
                                        >
                                            Cadastrar Contrato
                                        </Button>
                                    </TooltipSelectDisabled>
                                </Typography>
                            </Grid>
                        }
                    </Stack>
                </Grid>

                <ContractFilter
                    isOpenFilters={isOpenFilters}
                    isLoadingList={isLoading}
                />

                <Grid item xs={12}>
                    <TableContainer component={Paper}>
                        <Table aria-label="Contratos">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Nome do Contrato</TableCell>
                                    <TableCell>Período</TableCell>
                                    <TableCell>Prazo restante</TableCell>
                                    <TableCell>Mensalidade</TableCell>
                                    <TableCell>Colaboradores</TableCell>
                                    <TableCell>Usuários Administradores</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="center" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    <LoadingTableRow />
                                ) : contratos.length > 0 ? (
                                    contratos.map((contrato) => (
                                        <Row
                                            key={contrato.id}
                                            contrato={contrato}
                                            actionsAllowed={actionsAllowed}
                                            handleOpenEditForm={() => handleOpenEditForm(contrato)}
                                            onSuccess={carregaContratos}
                                        />
                                    ))
                                ) : (
                                    <EmptyTableRow colSpan={7} />
                                )}
                            </TableBody>
                        </Table>
                            <CustomTablePagination
                                numTotalItems={numTotalItems}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                setPage={setPage}
                                setRowsPerPage={setRowsPerPage}
                            />
                    </TableContainer>
                </Grid>

                <ContractForm
                    open={isOpenEditForm}
                    setOpen={setIsOpenEditForm}
                    contrato={selectedContrato}
                    actionsAllowed={actionsAllowed}
                    onSuccess={carregaContratos}
                />
            </Grid>
        </>
    );
}
